import { CircleAlert } from 'lucide-react'
import { createContext, type ReactNode, useContext, useId } from 'react'
import { cn } from '../lib/cn'

interface FieldContextValue {
  id: string
  describedBy: string | undefined
  invalid: boolean
  required: boolean
}

const FieldContext = createContext<FieldContextValue | null>(null)

export interface FieldControlProps {
  id?: string | undefined
  'aria-describedby'?: string | undefined
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling' | undefined
  'aria-required'?: boolean | 'true' | 'false' | undefined
}

/**
 * Wires a control to the enclosing Field: id (for the label), aria-describedby (hint/error) and
 * aria-invalid/aria-required. Explicit props win.
 */
export function useFieldControl(props: FieldControlProps): FieldControlProps {
  const field = useContext(FieldContext)
  if (!field) return {}
  return {
    id: props.id ?? field.id,
    'aria-describedby': cn(props['aria-describedby'], field.describedBy) || undefined,
    'aria-invalid': props['aria-invalid'] ?? (field.invalid || undefined),
    'aria-required': props['aria-required'] ?? (field.required || undefined),
  }
}

export interface FieldProps {
  label: ReactNode
  children: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  className?: string
  /** Visually hide the label (it stays available to assistive tech). */
  labelHidden?: boolean
  id?: string
}

export function Field({
  label,
  children,
  hint,
  error,
  required = false,
  className,
  labelHidden,
  id,
}: FieldProps) {
  const autoId = useId()
  const controlId = id ?? `field-${autoId}`
  const hintId = hint ? `${controlId}-hint` : undefined
  const errorId = error ? `${controlId}-error` : undefined
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      <label htmlFor={controlId} className={cn('text-sm font-medium text-ink', labelHidden && 'sr-only')}>
        {label}
        {required ? (
          <span aria-hidden className="ms-0.5 text-ink-red">
            *
          </span>
        ) : null}
      </label>
      <FieldContext
        value={{
          id: controlId,
          describedBy: cn(hintId, errorId) || undefined,
          invalid: Boolean(error),
          required,
        }}
      >
        {children}
      </FieldContext>
      {hint ? (
        <p id={hintId} className="text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="flex items-start gap-1 text-xs text-ink-red">
          <CircleAlert aria-hidden className="mt-px size-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  )
}

export interface FormSectionProps {
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  className?: string
  /** Two-column field grid on wide screens (default). */
  columns?: 1 | 2
  id?: string
  /** Beside the title (a button that belongs to the section, e.g. "Show the document" by the lines). */
  actions?: ReactNode
}

/** A titled group of fields on a surface. Collapses to one column below 640px. */
export function FormSection({
  title,
  description,
  children,
  className,
  columns = 2,
  id,
  actions,
}: FormSectionProps) {
  const headingId = useId()
  const heading = (
    <h2 id={headingId} className="text-lg font-semibold text-ink">
      {title}
    </h2>
  )
  return (
    <section
      id={id}
      aria-labelledby={headingId}
      className={cn('border-b border-rule px-4 py-5 last:border-b-0 sm:px-6', className)}
    >
      <div className="mb-4">
        {actions ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            {heading}
            {actions}
          </div>
        ) : (
          heading
        )}
        {description ? <p className="mt-0.5 text-sm text-ink-muted">{description}</p> : null}
      </div>
      <div className={cn('grid grid-cols-1 gap-x-4 gap-y-4', columns === 2 && 'sm:grid-cols-2')}>
        {children}
      </div>
    </section>
  )
}
